/**
 * useConversationContextMenu Hook
 *
 * Manages state for the Conversation Context modal triggered from the
 * ModerationActionMenu "Context" menu item.
 *
 * When the user clicks "Context", this hook first performs a content search
 * using the case's `subjectId` (same approach as Classification) to retrieve
 * the full `contentMetadata.metadata` from the Content Search API. This
 * metadata (e.g. world, server) is then used by the ConversationContextModal
 * to call the contextual-messages API — matching how Content Search's
 * "Show Conversation Context" flow works.
 *
 * Auto-closes the modal when the focused card changes so the user always
 * starts with a clean slate on the newly focused card.
 */

import type { ContentCardItem, ContentMetadata } from '@content-search/domain';
import { useContentSearch } from '@content-search/hooks';
import { createContentCardItem } from '@content-search/utils';
import type { DomainError } from '@core/domain';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ModerationCase } from '../domain/ModerationCase';

/** Parameters for the conversation context menu hook */
interface ConversationContextMenuParams {
  communityId: string;
  sessionCases: ModerationCase[];
  focusedCaseId: string | null;
}

/** All state & handlers the page needs for the context modal */
export interface ConversationContextMenuState {
  /** Whether the conversation context modal is open */
  isContextModalOpen: boolean;
  /** Open the context modal for the currently focused case */
  handleOpenContextModal: () => void;
  /** Close the context modal */
  handleCloseContextModal: () => void;
  /** Props to pass directly to ConversationContextModal (only valid when open) */
  contextModalProps: {
    communityId: string;
    subjectId: string;
    topicName: string;
    metadata: Record<string, unknown>;
    selectedMessage: ContentCardItem | undefined;
    isSearchingContent: boolean;
    contentSearchErrors: DomainError[] | null;
  };
}

/**
 * Hook that manages the Conversation Context modal state for the
 * moderation work queue.
 *
 * Performs a content search by subjectId (same as Classification) to obtain
 * the metadata needed for the contextual-messages API, then opens the modal
 * with that search-derived metadata and selectedMessage.
 */
export function useConversationContextMenu({ communityId, sessionCases, focusedCaseId }: ConversationContextMenuParams): ConversationContextMenuState {
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [searchDerivedMetadata, setSearchDerivedMetadata] = useState<Record<string, unknown> | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ContentCardItem | undefined>();

  const { search, searchResult, isSearching: isContentSearching, errors: contentSearchErrors } = useContentSearch();

  // Close the context modal and reset search state whenever the focused card changes
  useEffect(() => {
    setIsContextModalOpen(false);
    setSearchDerivedMetadata(null);
    setSelectedMessage(undefined);
  }, [focusedCaseId]);

  const focusedCase = useMemo(() => sessionCases.find(c => c.caseId === focusedCaseId) ?? null, [sessionCases, focusedCaseId]);

  // When content search completes, extract metadata and build selectedMessage
  useEffect(() => {
    if (!isContextModalOpen || !searchResult?.results?.length) return;

    const firstResult = searchResult.results[0];
    const contentMetadata = firstResult.contentMetadata as ContentMetadata;

    // Extract metadata from content search result (world, server, etc.)
    if (contentMetadata?.metadata) {
      setSearchDerivedMetadata(contentMetadata.metadata);
    }

    // Build a ContentCardItem for highlighting the target message in the modal
    const cardItem = createContentCardItem(firstResult, 0);

    setSelectedMessage(cardItem);
  }, [searchResult, isContextModalOpen]);

  const contextModalProps = useMemo(
    () => ({
      communityId,
      // Use top-level subjectId (same as Classification)
      subjectId: focusedCase?.subjectId ?? '',
      topicName: focusedCase?.report?.topicName ?? '',
      // Use metadata from content search result; empty object until search completes
      metadata: searchDerivedMetadata ?? {},
      selectedMessage,
      isSearchingContent: isContentSearching,
      contentSearchErrors,
    }),
    [communityId, focusedCase, searchDerivedMetadata, selectedMessage, isContentSearching, contentSearchErrors]
  );

  const handleOpenContextModal = useCallback(() => {
    const subjectId = focusedCase?.subjectId;

    if (!subjectId) return;

    // Reset state for fresh search
    setSearchDerivedMetadata(null);
    setSelectedMessage(undefined);
    setIsContextModalOpen(true);

    // Perform content search by subjectId — same approach as Classification
    search({ communityId, subjectId, maxResultsPerBatch: 1 }, 'network-only');
  }, [focusedCase, communityId, search]);

  const handleCloseContextModal = useCallback(() => {
    setIsContextModalOpen(false);
  }, []);

  return {
    isContextModalOpen,
    handleOpenContextModal,
    handleCloseContextModal,
    contextModalProps,
  };
}
